import type {
  Contract,
  ContractDepartmentUser,
  ContractParameterValue,
} from '@/lib/services/contractService';
import { getApiErrorCode, getApiErrorMessage } from '@/lib/apiError';
import type {
  ContractTemplateForContract,
  ContractTemplateItem,
  ContractTemplateParameter,
  ContractTemplateRequisite,
  InsertedTemplateParameter,
} from '@/lib/services/contractTemplateService';
export enum ContractSaveType {
  Create = 0,
  Draft = 1,
}

export enum ContractParameterType {
  Unknown = 0,
  Date = 1,
  Text = 2,
  Number = 3,
  Select = 4,
  MultiSelect = 5,
  Boolean = 6,
}

export enum ContractApprovalStatus {
  Draft = 0,
  Revision = 1,
  Approved = 2,
  Rejected = 3,
  Canceled = 4,
  Frozen = 5,
  Terminated = 6,
  Pending = 7,
  WaitingForLawyer = 8,
}

export enum ContractVersionType {
  Template = 1,
  File = 2,
}

export enum ContractEmployeesExistingType {
  CompanyAdded = 0,
  OrganizationAdded = 1,
  Both = 2,
}

export enum ContractApprovalStepRole {
  Lawyer = 1,
  User = 2,
  Director = 3,
  Added = 4,
}

export enum ContractApprovalStepSide {
  Organization = 1,
  Company = 2,
}

export enum ContractStepDecision {
  Pending = 0,
  Approve = 1,
  Reject = 2,
  Decline = 3,
  Revision = 4,
}

export enum ReasonForAbsenceType {
  Unknown = 0,
  Bulletin = 1,
  Vacation = 2,
}

export const getContractStepRoleKey = (role?: number | null) => {
  switch (Number(role)) {
    case ContractApprovalStepRole.Lawyer:
      return 'lawyer';
    case ContractApprovalStepRole.User:
      return 'user';
    case ContractApprovalStepRole.Director:
      return 'director';
    case ContractApprovalStepRole.Added:
      return 'added';
    default:
      return null;
  }
};

export const getContractStepDecisionKey = (decision?: number | null) => {
  switch (Number(decision)) {
    case ContractStepDecision.Approve:
      return '1';
    case ContractStepDecision.Reject:
      return '2';
    case ContractStepDecision.Decline:
      return '3';
    case ContractStepDecision.Revision:
      return '4';
    default:
      return '0';
  }
};

export const getReasonForAbsenceKey = (reason?: number | null) => {
  switch (Number(reason)) {
    case ReasonForAbsenceType.Bulletin:
      return 'bulletin';
    case ReasonForAbsenceType.Vacation:
      return 'vacation';
    default:
      return null;
  }
};

export const isFileContract = (contract?: { contractVersionType?: number | string } | null) =>
  Number(contract?.contractVersionType) === ContractVersionType.File;

export const canAddFileContractUsers = (contract?: {
  contractVersionType?: number;
  isCreatedByCompany?: boolean;
  contractEmployeesExistingType?: number;
} | null) =>
  isFileContract(contract) &&
  !contract?.isCreatedByCompany &&
  contract?.contractEmployeesExistingType ===
    ContractEmployeesExistingType.OrganizationAdded;

export const canEditCompanyFileDraft = (contract?: {
  contractVersionType?: number;
  isCreatedByCompany?: boolean;
  approvalStatus?: number;
} | null) =>
  isFileContract(contract) &&
  contract?.isCreatedByCompany === true &&
  Number(contract?.approvalStatus) === ContractApprovalStatus.Draft;

export const isOwnContractDraft = (contract?: {
  isCreatedByCompany?: boolean;
  approvalStatus?: number;
} | null) =>
  contract?.isCreatedByCompany === true &&
  Number(contract?.approvalStatus) === ContractApprovalStatus.Draft;

export const canCancelContract = (contract?: {
  isCreatedByCompany?: boolean;
  approvalStatus?: number;
} | null) =>
  contract?.isCreatedByCompany === true &&
  [ContractApprovalStatus.Draft, ContractApprovalStatus.WaitingForLawyer].includes(
    Number(contract?.approvalStatus)
  );

export const canVoteOnContract = (contract?: {
  approvalStatus?: number;
  contractVersionType?: number;
} | null) => {
  const status = Number(contract?.approvalStatus);
  if (
    status === ContractApprovalStatus.Pending ||
    status === ContractApprovalStatus.WaitingForLawyer
  ) {
    return true;
  }
  return status === ContractApprovalStatus.Revision && !isFileContract(contract);
};

export const hasFileContractVoting = (contract?: {
  approvalStatus?: number;
  isCreatedByCompany?: boolean;
} | null) => {
  const status = Number(contract?.approvalStatus);
  if (status === ContractApprovalStatus.Draft) return false;
  return (
    status !== ContractApprovalStatus.WaitingForLawyer ||
    contract?.isCreatedByCompany === true
  );
};

export enum ContractNumberSource {
  Document = 1,
  Generated = 2,
}

export const getContractApiErrorMessage = (error: unknown): string | undefined =>
  getApiErrorMessage(error) || undefined;

export enum RenewalPeriodUnit {
  Month = 1,
  Year = 2,
}

export const getContractVersionTypeKey = (type?: number | string) => {
  switch (Number(type)) {
    case ContractVersionType.File:
      return 'file';
    case ContractVersionType.Template:
      return 'template';
    default:
      return null;
  }
};

export const getContractEditHref = (
  locale: string,
  contract: { id: number; contractVersionType?: number }
) =>
  isFileContract(contract)
    ? `/${locale}/contracts/edit-fileContract/${contract.id}`
    : `/${locale}/contracts/${contract.id}`;

export enum ActionSignatureType {
  None = 0,
  Internal = 1,
  Sima = 2,
  Both = 3,
}

export const isSimaSignatureType = (value?: number | null) =>
  value === ActionSignatureType.Sima || value === ActionSignatureType.Both;

export const isOtherSideSignedFailure = (message?: string | null) =>
  /other side signed/i.test(message ?? "");

export enum CompanyDepartmentType {
  Unknown = 0,
  Finance = 1,
  Legal = 2,
  Purchase = 3,
  ExecutiveDirector = 4,
}

export const getSignatureTypeByDepartment = (department?: number) =>
  department === CompanyDepartmentType.ExecutiveDirector
    ? ActionSignatureType.Sima
    : ActionSignatureType.Internal;

export const isContractNotFoundError = (error: unknown) =>
  getApiErrorCode(error) === 404;

export const FILE_CONTRACT_USERS_MODE = "users";

export const getFileContractUsersHref = (locale: string, id: number) =>
  `/${locale}/contracts/edit-fileContract/${id}?mode=${FILE_CONTRACT_USERS_MODE}`;

export const getContractViewHref = (
  locale: string,
  contract: { id: number; contractVersionType?: number },
  fallbackHref?: string
) =>
  isFileContract(contract)
    ? `/${locale}/contracts/view-fileContract/${contract.id}`
    : fallbackHref || `/${locale}/contracts/view?id=${contract.id}`;

export const APPROVAL_STATUS_OPTIONS = [
  { value: ContractApprovalStatus.Draft, key: 'draft' },
  { value: ContractApprovalStatus.Revision, key: 'revision' },
  { value: ContractApprovalStatus.Approved, key: 'approved' },
  { value: ContractApprovalStatus.Rejected, key: 'rejected' },
  { value: ContractApprovalStatus.Canceled, key: 'canceled' },
  { value: ContractApprovalStatus.Frozen, key: 'frozen' },
  { value: ContractApprovalStatus.Terminated, key: 'terminated' },
  { value: ContractApprovalStatus.Pending, key: 'pending' },
  { value: ContractApprovalStatus.WaitingForLawyer, key: 'waitingForLawyer' },
] as const;

export const getApprovalStatusKey = (status?: number | string) => {
  const value = Number(status);

  switch (value) {
    case ContractApprovalStatus.Draft:
      return 'draft';
    case ContractApprovalStatus.Revision:
      return 'revision';
    case ContractApprovalStatus.Approved:
      return 'approved';
    case ContractApprovalStatus.Rejected:
      return 'rejected';  
    case ContractApprovalStatus.Canceled:
      return 'canceled';
    case ContractApprovalStatus.Frozen:
      return 'frozen';
    case ContractApprovalStatus.Terminated:
      return 'terminated';
    case ContractApprovalStatus.Pending:
      return 'pending';
    case ContractApprovalStatus.WaitingForLawyer:
      return 'waitingForLawyer';
    default:
      return 'draft';
  }
};

export const isTerminationProcessStarted = (contract?: {
  isTerminationProccessStarted?: boolean | null;
  IsTerminationProccessStarted?: boolean | null;
} | null) =>
  contract?.isTerminationProccessStarted === true ||
  contract?.IsTerminationProccessStarted === true;

export const getContractParameterValue = (
  parameters: Array<{ name?: string; value?: string }> | undefined,
  name: string
) => parameters?.find((parameter) => parameter.name === name)?.value || '-';

export const getTemplateTitle = (
  template?: { title?: string; name?: string } | null
) => template?.title || template?.name || '';

export const getTemplateNumber = (
  template?: { templateNumber?: string; code?: string; id?: number } | null
) => template?.templateNumber || template?.code || (template?.id ? `CT-${template.id}` : '');

export const getTemplateCategoryName = (
  template?: {
    contractTemplateCategory?: { name?: string };
    categoryName?: string;
  } | null
) => template?.contractTemplateCategory?.name || template?.categoryName || '';

export const getTemplateHtml = (template?: ContractTemplateForContract | null) => {
  if (!template) return '';
  const record = template as ContractTemplateForContract & Record<string, unknown>;
  const nested = (record.contractTemplate || record.template) as
    | ContractTemplateForContract
    | undefined;

  return String(
    template.body ||
      template.htmlTemplate ||
      template.templateText ||
      template.content ||
      template.text ||
      record.html ||
      nested?.body ||
      nested?.htmlTemplate ||
      nested?.templateText ||
      nested?.content ||
      nested?.text ||
      ''
  );
};

const getRawParameters = (source?: ContractTemplateForContract | null) => {
  if (!source) return undefined;
  const record = source as ContractTemplateForContract & Record<string, unknown>;
  const candidates = [
    source.parameters,
    record.parameterDtos,
    record.contractParameters,
    record.parameterSets,
  ];
  return candidates.find((item) => Array.isArray(item) && item.length > 0) as
    | ContractTemplateParameter[]
    | undefined;
};

export const getTemplateParameters = (
  template?: ContractTemplateForContract | null
): ContractTemplateParameter[] => {
  if (!template) return [];
  const record = template as ContractTemplateForContract & Record<string, unknown>;
  const nested = (record.contractTemplate || record.template) as
    | ContractTemplateForContract
    | undefined;
  const raw = getRawParameters(template) || getRawParameters(nested) || [];

  return raw
    .map((item) => {
      const binding = item as ContractTemplateParameter & Record<string, unknown>;
      const nestedParameter = binding.parameter as
        | { id?: number; name?: string; type?: number }
        | undefined;

      return {
        id: Number(nestedParameter?.id ?? binding.id ?? binding.parameterId ?? 0),
        name: String(
          nestedParameter?.name ?? binding.name ?? binding.title ?? binding.label ?? ''
        ),
        type: Number(nestedParameter?.type ?? binding.type ?? binding.parameterType ?? 0),
        isRequired: Boolean(binding.isRequired ?? binding.required),
        isVisible: binding.isVisible as boolean | undefined,
        options: (binding.options ||
          binding.values ||
          binding.parameterValues) as ContractTemplateParameter['options'],
      };
    })
    .filter((parameter) => parameter.id && parameter.isVisible !== false);
};

export const getInsertedParameters = (
  template?: ContractTemplateForContract | null
): InsertedTemplateParameter[] => {
  if (!template) return [];
  const record = template as ContractTemplateForContract & Record<string, unknown>;
  const nested = (record.contractTemplate || record.template) as
    | ContractTemplateForContract
    | undefined;
  const list = template.insertedParameters || nested?.insertedParameters;

  if (!Array.isArray(list)) return [];

  return list
    .map((item) => ({
      parameterId: Number(item.parameterId || 0),
      parameterName: item.parameterName,
      value: item.value || '',
    }))
    .filter((item) => item.parameterId);
};

export const buildContractParameterValues = (
  parameters: ContractTemplateParameter[],
  parameterValues: Record<number, string>,
  insertedParameters: InsertedTemplateParameter[] = []
): ContractParameterValue[] => {
  const formValues = parameters.map((parameter) => {
    const type = inferParameterType(parameter);
    const value = parameterValues[parameter.id] || '';
    const matchedOption = parameter.options?.find(
      (option) => String(option.id) === value || option.name === value
    );

    return {
      parameterId: parameter.id,
      type,
      value,
      values: value ? [value] : [],
      valueIds: matchedOption ? [matchedOption.id] : [],
    };
  });

  const formIds = new Set(formValues.map((item) => item.parameterId));
  const insertedValues = insertedParameters
    .filter((item) => !formIds.has(item.parameterId))
    .map((item) => ({
      parameterId: item.parameterId,
      type: ContractParameterType.Text,
      value: item.value || '',
      values: item.value ? [item.value] : [],
      valueIds: [] as number[],
    }));

  return [...formValues, ...insertedValues];
};

export const normalizeTemplate = (
  template?: ContractTemplateForContract | null
): ContractTemplateForContract | null => {
  if (!template) return null;
  const record = template as ContractTemplateForContract & Record<string, unknown>;
  const nested = (record.contractTemplate || record.template) as
    | ContractTemplateForContract
    | undefined;

  return {
    ...nested,
    ...template,
    id: template.id || nested?.id || 0,
    name: getTemplateTitle(template) || getTemplateTitle(nested),
    title: getTemplateTitle(template) || getTemplateTitle(nested),
    templateNumber: getTemplateNumber(template) || getTemplateNumber(nested),
    categoryName: getTemplateCategoryName(template) || getTemplateCategoryName(nested),
    categoryId:
      template.categoryId ||
      template.contractTemplateCategory?.id ||
      nested?.categoryId ||
      nested?.contractTemplateCategory?.id,
    parameters: getTemplateParameters(template),
    insertedParameters: getInsertedParameters(template),
    htmlTemplate: getTemplateHtml(template),
  };
};

const asRequisiteList = (value: unknown): ContractTemplateRequisite[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is ContractTemplateRequisite => {
    return Boolean(item && typeof item === 'object' && 'id' in item);
  });
};

export const getCompanyRequisites = (
  template?: ContractTemplateForContract | null
): ContractTemplateRequisite[] => {
  if (!template) return [];
  const record = template as ContractTemplateForContract & Record<string, unknown>;
  return asRequisiteList(
    record.companyRequisites ||
      record.companyAccountings ||
      record.companyAccounting ||
      record.companyRequisite
  );
};

export const getOrganizationRequisites = (
  template?: ContractTemplateForContract | null
): ContractTemplateRequisite[] => {
  if (!template) return [];
  const record = template as ContractTemplateForContract & Record<string, unknown>;
  return asRequisiteList(
    record.organizationRequisites ||
      record.organisationRequisites ||
      record.organizationAccountings ||
      record.organizationAccounting
  );
};

export const formatRequisiteLabel = (requisite: ContractTemplateRequisite) => {
  const bankName =
    requisite.bank?.name ||
    requisite.bank?.shortName ||
    requisite.bankName ||
    requisite.name ||
    '';
  const account = requisite.account || '';
  const primary = requisite.isPrimary ? ' (Primary)' : '';

  if (bankName && account) return `${bankName} - ${account}${primary}`;
  if (bankName) return `${bankName}${primary}`;
  if (account) return `${account}${primary}`;
  return String(requisite.id);
};

export interface SelectedContractItem {
  id: number;
  name: string;
  kind: 'product' | 'service';
}

export const getTemplateItems = (
  template?: ContractTemplateForContract | null
): SelectedContractItem[] => {
  if (!template) return [];

  const toItems = (
    items: ContractTemplateItem[] | undefined,
    kind: SelectedContractItem['kind']
  ) =>
    (items || []).map((item) => ({
      id: item.id,
      name: item.name || item.title || String(item.id),
      kind,
    }));

  const products = toItems(template.products, 'product');
  const services = toItems(template.services, 'service');
  const generic = (template.items || []).map((item) => {
    const type = String(item.type ?? '').toLowerCase();
    const kind: SelectedContractItem['kind'] =
      type.includes('service') || type === '2' ? 'service' : 'product';
    return {
      id: item.id,
      name: item.name || item.title || String(item.id),
      kind,
    };
  });

  return [...products, ...services, ...generic];
};

export const inferParameterType = (parameter: ContractTemplateParameter) => {
  if (parameter.type && parameter.type > 0) return parameter.type;

  const name = parameter.name?.toLowerCase() || '';
  if (name.includes('tarix') || name.includes('date')) return ContractParameterType.Date;
  if (name.includes('say') || name.includes('number') || name.includes('məbləğ')) {
    return ContractParameterType.Number;
  }
  if (parameter.options && parameter.options.length > 0) return ContractParameterType.Select;
  return ContractParameterType.Text;
};

const padDatePart = (value: number) => String(value).padStart(2, '0');

const toLocalCalendarDate = (year: number, month: number, day: number) => {
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

/** Template parameter dates: DD.MM.YYYY */
export const formatDateForApi = (date?: Date) => {
  if (!date) return '';
  return `${padDatePart(date.getDate())}.${padDatePart(date.getMonth() + 1)}.${date.getFullYear()}`;
};

/** Contract start/end dates: local calendar day, no UTC shift */
export const formatDateTimeForApi = (date?: Date) => {
  if (!date) return '';
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}T00:00:00`;
};

export const parseApiDate = (value?: string) => {
  if (!value) return undefined;

  const dotted = value.split('.');
  if (dotted.length === 3 && !value.includes('T')) {
    const [day, month, year] = dotted.map(Number);
    return toLocalCalendarDate(year, month, day);
  }

  const isoDate = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    const hasTimeZone = /Z$|[+-]\d{2}:\d{2}$/.test(value);
    if (hasTimeZone) {
      const zoned = new Date(value);
      if (Number.isNaN(zoned.getTime())) return undefined;
      return toLocalCalendarDate(
        zoned.getFullYear(),
        zoned.getMonth() + 1,
        zoned.getDate()
      );
    }
    return toLocalCalendarDate(
      Number(isoDate[1]),
      Number(isoDate[2]),
      Number(isoDate[3])
    );
  }

  const fallback = new Date(value);
  if (Number.isNaN(fallback.getTime())) return undefined;
  return toLocalCalendarDate(
    fallback.getFullYear(),
    fallback.getMonth() + 1,
    fallback.getDate()
  );
};

export const formatDateForDisplay = (value?: string) => {
  const date = parseApiDate(value);
  if (!date) return value || '-';
  return date.toLocaleDateString('az-AZ');
};

export const getContractTemplateId = (contract?: Contract | null) => {
  if (!contract) return 0;
  const record = contract as Contract & Record<string, unknown>;
  const nested = (record.contractTemplate ||
    record.template ||
    record.contractTemplateDto) as { id?: number } | undefined;

  return Number(
    contract.contractTemplateId ||
      record.ContractTemplateId ||
      contract.templateId ||
      record.TemplateId ||
      nested?.id ||
      0
  );
};

export const normalizeTemplateFromContract = (
  contract?: Contract | null
): ContractTemplateForContract | null => {
  if (!contract) return null;

  const parameters = [...(contract.parameters || [])]
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((item) => ({
      id: item.id,
      name: item.name || String(item.id),
      type: 0,
      isVisible: true,
    }));

  return {
    id: getContractTemplateId(contract) || contract.id,
    name: contract.contractTemplateName || contract.contractTemplate?.name || contract.number,
    title: contract.contractTemplateName || contract.contractTemplate?.name || contract.number,
    categoryId: contract.contractTemplateCategoryId || contract.category?.id,
    categoryName: contract.category?.name || contract.categoryName,
    parameters,
    htmlTemplate: contract.originalHtml || contract.renderedHtml || '',
    insertedParameters: (contract.values || []).map((item) => ({
      parameterId: item.parameterId,
      value: item.value || item.values?.[0] || '',
    })),
  };
};

export const getContractParameterValuesMap = (
  contract?: Contract | null,
  parameters: ContractTemplateParameter[] = []
): Record<number, string> => {
  const values: Record<number, string> = {};
  if (!contract) return values;

  const applyValue = (parameterId: number, raw?: string, valuesList?: string[]) => {
    if (!parameterId) return;
    const rawValue = raw || valuesList?.[0] || '';
    if (!rawValue && values[parameterId]) return;

    const parameter = parameters.find((item) => item.id === parameterId);
    const matchedOption = parameter?.options?.find(
      (option) => String(option.id) === rawValue || option.name === rawValue
    );

    values[parameterId] = matchedOption ? String(matchedOption.id) : rawValue;
  };

  contract.values?.forEach((item) => {
    applyValue(item.parameterId, item.value, item.values);
  });
  contract.parameters?.forEach((item) => {
    applyValue(item.id, item.value, item.values);
  });

  return values;
};

export const getContractSelectedItems = (
  contract?: Contract | null
): SelectedContractItem[] => {
  if (!contract) return [];

  const products = (contract.products || []).map((item) => ({
    id: item.productId,
    name: item.productName || String(item.productId),
    kind: 'product' as const,
  }));
  const services = (contract.services || []).map((item) => ({
    id: item.serviceId,
    name: item.serviceName || String(item.serviceId),
    kind: 'service' as const,
  }));

  return [...products, ...services].filter((item) => item.id);
};

const getDepartmentUserName = (user: ContractDepartmentUser) =>
  user.userName ||
  [user.name, user.surname].filter(Boolean).join(' ').trim() ||
  user.email ||
  user.userId;

export const getContractDepartmentBlocks = (
  contract?: Contract | null
): Array<{
  departmentId: number;
  selectedUserId?: string;
  users: Array<{
    userId: string;
    userName: string;
    actionSignatureType: number;
  }>;
}> => {
  if (!contract?.departments) return [];

  return [...contract.departments]
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((department) => {
      const users = [...(department.users || [])]
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((user) => ({
          userId: user.userId,
          userName: getDepartmentUserName(user),
          actionSignatureType: user.actionSignatureType || 1,
        }));

      return {
        departmentId: department.departmentId,
        selectedUserId: users[0]?.userId,
        users,
      };
    })
    .filter((item) => item.departmentId);
};
